import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import DynamicIcon from "../DynamicIcon";
import { selectSelectedCategory, selectSelectedCategoryLoading, deleteProductCategory, selectDeleteCategoryLoading, selectDeleteCategoryError, resetDeleteStatus, selectProductCategoriesList } from "../../redux/productCategory/productCategorySlice";
import { closeModal, setHasToRefresh, setGoingToClose, setModal } from "../../redux/rightModalSlice";
import { selectAccountData } from "../../redux/main/accountSlice";
import LoadingSection from "../LoadingSection";

export default function ViewCategory() {
    const dispatch = useDispatch();
    const selectedCategory = useSelector(selectSelectedCategory);
    const selectedCategoryLoading = useSelector(selectSelectedCategoryLoading);
    const deleteLoading = useSelector(selectDeleteCategoryLoading);
    const deleteError = useSelector(selectDeleteCategoryError);
    const categoriesList = useSelector(selectProductCategoriesList);
    const accountData = useSelector(selectAccountData);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteOption, setDeleteOption] = useState('categoryOnly');
    const token = localStorage.getItem('installamelo-auth-token');

    // Prodotti associati alla categoria
    const [categoryProducts, setCategoryProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);

    const loadCategoryProducts = useCallback(async () => {
        if (!selectedCategory?._id || !accountData?._id || !token) return;
        setProductsLoading(true);
        try {
            const res = await fetch(
                `${import.meta.env.VITE_API_BASE_URL}/products/categoryProducts?accountId=${accountData._id}&categoryId=${selectedCategory._id}`,
                { headers: { 'auth-token': token } }
            );
            const data = await res.json();
            setCategoryProducts(Array.isArray(data) ? data : (data.data || []));
        } catch {
            setCategoryProducts([]);
        } finally {
            setProductsLoading(false);
        }
    }, [selectedCategory?._id, accountData?._id, token]);

    useEffect(() => {
        loadCategoryProducts();
    }, [loadCategoryProducts]);

    const parentCategory = selectedCategory?.parentId
        ? categoriesList.find(cat => cat._id === selectedCategory.parentId)
        : null;

    const childrenCount = selectedCategory?._id
        ? categoriesList.filter(cat => cat.parentId === selectedCategory._id).length
        : 0;

    const childrenNames = selectedCategory?._id
        ? categoriesList.filter(cat => cat.parentId === selectedCategory._id).map(c => c.name)
        : [];

    const handleEditClick = () => {
        dispatch(setModal({ mode: 'edit', id: selectedCategory._id, percentuageWidth: 40 }));
    };

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };

    const confirmDelete = () => {
        if (selectedCategory?._id) {
            dispatch(deleteProductCategory({
                categoryId: selectedCategory._id,
                deleteProducts: deleteOption === 'withProducts',
                token
            })).then((result) => {
                if (!result.error) {
                    dispatch(setHasToRefresh());
                    dispatch(setGoingToClose());
                }
            });
        }
    };

    // Conta ricorsivamente le sottocategorie
    const countAllSubcategories = (catId) => {
        const directChildren = categoriesList.filter(c => c.parentId === catId);
        let count = directChildren.length;
        directChildren.forEach(child => { count += countAllSubcategories(child._id); });
        return count;
    };

    const totalSubcategories = selectedCategory?._id ? countAllSubcategories(selectedCategory._id) : 0;

    useEffect(() => {
        return () => { dispatch(resetDeleteStatus()); };
    }, [dispatch]);

    if (selectedCategoryLoading) {
        return <LoadingSection />;
    }

    if (!selectedCategory) {
        return (
            <div className="p-5">
                <p className="text-gray-500 text-sm">Categoria non trovata.</p>
                <button className="secondary mt-3" onClick={() => dispatch(closeModal())}>
                    <DynamicIcon iconClass="fas fa-arrow-left" /> Indietro
                </button>
            </div>
        );
    }

    return (
        <div className="p-5">
            {deleteLoading && <LoadingSection isFixed />}

            {/* Header */}
            <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center flex-shrink-0">
                        <DynamicIcon iconClass="fa-solid fa-folder text-yellow-600" />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 leading-tight">{selectedCategory.name}</h2>
                        <div className="flex items-center gap-2 mt-0.5">
                            {selectedCategory.active !== false ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                    Attiva
                                </span>
                            ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                    Inattiva
                                </span>
                            )}
                            {parentCategory && (
                                <span className="text-xs text-gray-500">
                                    in {parentCategory.name}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button className="secondary" onClick={handleEditClick}>
                        <DynamicIcon iconClass="fas fa-pen" /> Modifica
                    </button>
                    <button className="danger" onClick={handleDeleteClick}>
                        <DynamicIcon iconClass="fas fa-trash" /> Elimina
                    </button>
                </div>
            </div>

            {/* Contenuto */}
            <div className="space-y-4">
                {/* Descrizione */}
                {selectedCategory.description && (
                    <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Descrizione</p>
                        <p className="text-sm text-gray-700">{selectedCategory.description}</p>
                    </div>
                )}

                {/* Gerarchia */}
                <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Gerarchia</p>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <p className="text-xs text-gray-500">Genitore</p>
                            <p className="text-sm font-medium text-gray-800">
                                {parentCategory ? parentCategory.name : "Nessuna (root)"}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500">Sottocategorie</p>
                            <p className="text-sm font-medium text-gray-800">{childrenCount}</p>
                        </div>
                    </div>
                    {childrenNames.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                            <div className="flex flex-wrap gap-1.5">
                                {childrenNames.map((name, i) => (
                                    <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-100">
                                        {name}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Prodotti associati */}
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            Prodotti associati {!productsLoading && `(${categoryProducts.length})`}
                        </p>
                    </div>
                    {productsLoading ? (
                        <div className="flex items-center justify-center py-6">
                            <DynamicIcon iconClass="fas fa-spinner fa-spin text-gray-400 text-lg" />
                        </div>
                    ) : categoryProducts.length === 0 ? (
                        <div className="bg-gray-50 rounded-lg p-4 text-center">
                            <p className="text-sm text-gray-400">Nessun prodotto in questa categoria.</p>
                        </div>
                    ) : (
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Codice</th>
                                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Nome</th>
                                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Matricola</th>
                                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Fornitore</th>
                                        <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Qta</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {categoryProducts.map((prod) => (
                                        <tr key={prod._id} className="hover:bg-gray-50">
                                            <td className="px-3 py-2 text-gray-600 font-mono text-xs">{prod.code || '-'}</td>
                                            <td className="px-3 py-2 text-gray-800 font-medium">{prod.title || '-'}</td>
                                            <td className="px-3 py-2 text-gray-600 text-xs">{prod.serialNumber || '-'}</td>
                                            <td className="px-3 py-2 text-gray-600 text-xs">{prod.supplierLabel || '-'}</td>
                                            <td className="px-3 py-2 text-right font-medium text-gray-800">{prod.quantity ?? 0}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end pt-4 mt-4 border-t border-gray-200">
                <button className="secondary" onClick={() => dispatch(closeModal())}>
                    <DynamicIcon iconClass="fas fa-times" /> Chiudi
                </button>
            </div>

            {/* Dialog conferma eliminazione */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-5 max-w-md w-full shadow-2xl">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                                <DynamicIcon iconClass="fas fa-exclamation-triangle text-red-600" />
                            </div>
                            <div>
                                <h3 className="text-base font-bold text-gray-900">Elimina categoria</h3>
                                <p className="text-xs text-gray-500">Azione irreversibile</p>
                            </div>
                        </div>

                        <p className="text-sm text-gray-600 mb-4">
                            Stai per eliminare <span className="font-semibold">"{selectedCategory.name}"</span>
                            {totalSubcategories > 0 && (
                                <> e le sue <span className="font-semibold">{totalSubcategories} sottocategorie</span></>
                            )}.
                            Scegli cosa fare con i prodotti associati:
                        </p>

                        {/* Opzioni di eliminazione */}
                        <div className="space-y-2 mb-4">
                            <label
                                className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${deleteOption === 'categoryOnly' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                                onClick={() => setDeleteOption('categoryOnly')}
                            >
                                <input
                                    type="radio"
                                    name="deleteOption"
                                    value="categoryOnly"
                                    checked={deleteOption === 'categoryOnly'}
                                    onChange={() => setDeleteOption('categoryOnly')}
                                    className="mt-0.5"
                                />
                                <div>
                                    <p className="text-sm font-semibold text-gray-900">Solo categoria{totalSubcategories > 0 ? ' e sottocategorie' : ''}</p>
                                    <p className="text-xs text-gray-500">I prodotti associati non verranno eliminati, resteranno senza categoria</p>
                                </div>
                            </label>

                            <label
                                className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${deleteOption === 'withProducts' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}
                                onClick={() => setDeleteOption('withProducts')}
                            >
                                <input
                                    type="radio"
                                    name="deleteOption"
                                    value="withProducts"
                                    checked={deleteOption === 'withProducts'}
                                    onChange={() => setDeleteOption('withProducts')}
                                    className="mt-0.5"
                                />
                                <div>
                                    <p className="text-sm font-semibold text-red-700">Categoria{totalSubcategories > 0 ? ', sottocategorie' : ''} e prodotti</p>
                                    <p className="text-xs text-gray-500">Verranno eliminati anche tutti i prodotti associati alla categoria{totalSubcategories > 0 ? ' e alle sottocategorie' : ''}</p>
                                </div>
                            </label>
                        </div>

                        {deleteError && (
                            <div className="mb-3 p-2 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs">
                                {typeof deleteError === 'object' ? deleteError.message || JSON.stringify(deleteError) : deleteError}
                            </div>
                        )}

                        <div className="flex justify-end gap-2">
                            <button
                                className="secondary"
                                onClick={() => { setShowDeleteConfirm(false); setDeleteOption('categoryOnly'); }}
                                disabled={deleteLoading}
                            >
                                Annulla
                            </button>
                            <button
                                className="danger"
                                onClick={confirmDelete}
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? (
                                    <><DynamicIcon iconClass="fas fa-spinner fa-spin" /> Eliminazione...</>
                                ) : (
                                    <><DynamicIcon iconClass="fas fa-trash" /> Elimina</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
